import { useState } from 'react';
import _ from 'lodash';
import { toastr } from 'react-redux-toastr';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Grid, Button, Accordion, AccordionSummary, AccordionDetails, AccordionActions, Divider, Avatar, Typography, TextField,
} from '@material-ui/core';
import AutoComplete from '@material-ui/lab/Autocomplete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UPDATE_DRAFT_BIDDING_INFORMATION, saveBidInformationDraft, UPDATE_DRAFT_BID_PROFILE_OBJECTS } from '../../store/bidProfileDraft';
import { UPDATE_BIDDING_INFORMATION, saveBidInformation } from '../../store/bidProfile';
import { BidProfileStatus } from '../../../Enums';
import SetupPreview from './SetupPreview';

import './BaseInfo.scss';

const Fields = Object.freeze({
    Name: 'name',
    Description: 'description',
    BidSetupId: 'bidSetupId',
});

function BaseInfo() {
    const dispatch = useDispatch();
    const { t } = useTranslation('shiftBidding');

    /** @type {ShiftBidding.ManagerStore} */
    // @ts-ignore
    const { managerAppData, bidProfileDraft, bidProfile } = useSelector((state) => state);
    const { managerBidSetups, bidProfileDrafts, bidProfiles } = managerAppData;

    const [validationErrors, setValidationErrors] = useState({ name: false, description: false, bidSetupId: false });

    /**
     * @param {string} name
     */
    const validateName = (name) => name && name.trim();

    /**
     * @param {string} desc
     */
    const validateDescription = (desc) => true;

    /**
     * @param {number} bidSetupId
     */
    const validateBidSetup = (bidSetupId) => {
        if (!bidSetupId) {
            return false;
        }
        const value = managerBidSetups.find((config) => config.bidSetupId === bidSetupId);
        if (!value) {
            return false;
        }
        return true;
    };

    /**
     * @param {string} field
     * @param {string | number} value
     */
    const removeValidationError = (field, value) => {
        switch (field) {
            case Fields.Name:
                if (validationErrors.name) {
                    if (validateName(value)) {
                        const errors = { ...validationErrors, name: false };
                        setValidationErrors(errors);
                    }
                }
                break;
            case Fields.Description:
                if (validationErrors.description) {
                    if (validateDescription(value)) {
                        const errors = { ...validationErrors, description: false };
                        setValidationErrors(errors);
                    }
                }
                break;
            case Fields.BidSetupId:
                if (validationErrors.bidSetupId) {
                    // @ts-ignore
                    if (validateBidSetup(value)) {
                        const errors = { ...validationErrors, bidSetupId: false };
                        setValidationErrors(errors);
                    }
                }
                break;
            default:
                break;
        }
    };

    /**
     * @param {BiddingDraft.IBidProfileDraft | BidProfile.IBidProfile} profile
     * @returns
     */
    const validate = (profile) => {
        let hasError = false;
        const errors = { name: false, description: false, bidSetupId: false };
        const { name, description, bidSetupId } = profile.bidProfileInformation;

        if (!validateName(name)) {
            hasError = true;
            errors.name = true;
        }

        if (!validateDescription(description)) {
            hasError = true;
            errors.description = true;
        }

        if (!validateBidSetup(bidSetupId)) {
            hasError = true;
            errors.bidSetupId = true;
        }

        if (errors.name !== validationErrors.name
            || errors.description !== validationErrors.description
            || errors.bidSetupId !== validationErrors.bidSetupId) {
            setValidationErrors(errors);
        }

        return !hasError;
    };

    /**
     * @param {string} target
     * @param {string} value
     */
    // @ts-ignore
    // eslint-disable-next-line no-unused-vars
    let handleInputChange = (target, value) => { };

    let handleConfirm = () => { };
    let handleDiscard = () => { };

    /**
     * @param {boolean} expanded
     */
    // @ts-ignore
    // eslint-disable-next-line no-unused-vars
    let toggleAccordion = (expanded) => { };

    /** @type {BiddingDraft.IBidProfileDraft | BidProfile.IBidProfile} */
    let profile;
    if (bidProfileDraft.id !== '') {
        profile = bidProfileDraft;

        handleInputChange = (target, value) => {
            removeValidationError(target, value);
            dispatch({
                type: UPDATE_DRAFT_BIDDING_INFORMATION,
                payload: {
                    [target]: value,
                },
            });
        };

        handleConfirm = () => {
            if (validate(bidProfileDraft)) {
                // @ts-ignore
                dispatch(saveBidInformationDraft(
                    bidProfileDraft.draftId,
                    {
                        bidProfileDrafts,
                        // @ts-ignore
                        bidProfileInformation: bidProfileDraft.bidProfileInformation,
                    },
                ));
            }
        };

        handleDiscard = () => {
            const name = profile.originalBidProfileInformation.name || '';
            const description = profile.originalBidProfileInformation.description || '';

            dispatch({
                type: UPDATE_DRAFT_BIDDING_INFORMATION,
                payload: _.cloneDeep({
                    ...profile.originalBidProfileInformation, name, description, isExpanded: true,
                }),
            });
        };

        toggleAccordion = (expanded) => {
            dispatch({ type: UPDATE_DRAFT_BIDDING_INFORMATION, payload: { isExpanded: expanded } });
        };
    } else if (bidProfile.id !== '') {
        profile = bidProfile;

        handleInputChange = (target, value) => {
            removeValidationError(target, value);
            dispatch({
                type: UPDATE_BIDDING_INFORMATION,
                payload: {
                    [target]: value,
                },
            });
        };

        handleConfirm = () => {
            if (validate(bidProfile)) {
                dispatch(saveBidInformation(
                    bidProfile.profileId,
                    {
                        bidProfiles,
                        bidProfileInformation: bidProfile.bidProfileInformation,
                    },
                ));
            }
        };

        handleDiscard = () => {
            const description = profile.originalBidProfileInformation.description || '';

            dispatch({
                type: UPDATE_BIDDING_INFORMATION,
                payload: _.cloneDeep({ ...profile.originalBidProfileInformation, description, isExpanded: true }),
            });
        };

        toggleAccordion = (expanded) => {
            dispatch({ type: UPDATE_BIDDING_INFORMATION, payload: { isExpanded: expanded } });
        };
    }

    // @ts-ignore
    if (profile === undefined) {
        return <></>;
    }
    const {
        bidProfileInformation,
        isCreationAllowed,
        isViewAllowed,
        status,
    } = profile;

    const {
        name,
        description,
        bidSetupId,
        isExpanded,
        isEditable,
    } = bidProfileInformation;
    
    /**
     * @param {string} target
     * @param {number} selectedBidSetupId
     */
    const handleBidSetupChange = (target, selectedBidSetupId) => {
        if (profile.status !== BidProfileStatus.Draft) {
            toastr.error('You cannot modify the bid setup of a bid', '');
        }
        removeValidationError(target, selectedBidSetupId);
        const setup = managerBidSetups.find((x) => x.bidSetupId === selectedBidSetupId);
        dispatch({
            type: UPDATE_DRAFT_BID_PROFILE_OBJECTS,
            payload: {
                general: {
                    anchorDay: setup?.anchorDay,
                    isRebidAllowed: setup?.isRebidAllowed,
                },
                bidProfileInformation: {
                    bidSetupId: selectedBidSetupId,
                    bidProcessType: setup?.bidProcessType,
                    scheduleSource: setup?.scheduleSource,
                },
            },
        });
    };

    const selectedSetup = managerBidSetups.find((config) => config.bidSetupId === bidSetupId);
    const createAccessSetups = managerBidSetups.filter((x) => x.canCreateBidProfile);

    const hasOnlyViewAccess = isViewAllowed && !isCreationAllowed;
    const isInputDisabled = !isEditable || hasOnlyViewAccess; // Already checks if profile is a rebid
    const disabledSetupStatus = [BidProfileStatus.Scheduled, BidProfileStatus.Active, BidProfileStatus.Closed,
        BidProfileStatus.Published, BidProfileStatus.Canceled];
    const isBidSetupDisabled = profile.isRebid || disabledSetupStatus.includes(status) || hasOnlyViewAccess;
    const disabledDescriptionStatus = [BidProfileStatus.Closed, BidProfileStatus.Published, BidProfileStatus.Canceled];
    const isDescriptionDisabled = disabledDescriptionStatus.includes(status) || hasOnlyViewAccess;

    return (
        <Accordion
            expanded={isExpanded}
            // @ts-ignore
            onChange={(event, expanded) => toggleAccordion(expanded)}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
            >
                <Avatar className="bde-avatar">1</Avatar>
                <Typography variant="h6">{t('form.baseInfoHeader')}</Typography>
            </AccordionSummary>
            <Divider />
            <AccordionDetails>
                <Grid
                    container
                    justifyContent="space-between"
                >
                    <Grid
                        container
                        spacing={2}
                        item
                        xs={6}
                    >
                        <Grid
                            item
                            xs={12}
                        >
                            <TextField
                                label={t('form.name')}
                                // @ts-ignore
                                error={validationErrors.name}
                                value={name}
                                onChange={(event) => handleInputChange(Fields.Name, event.target.value)}
                                inputProps={{ maxLength: 50 }}
                                placeholder={t('form.name')}
                                variant="outlined"
                                disabled={isInputDisabled}
                            />
                            {
                                validationErrors.name ? <span style={{ color: 'red' }}>{t('form.error.name')}</span> : null
                            }
                        </Grid>
                        <Grid
                            item
                            xs={12}
                        >
                            <TextField
                                label={t('form.description')}
                                // @ts-ignore
                                error={validationErrors.description}
                                value={description}
                                onChange={(event) => handleInputChange(Fields.Description, event.target.value)}
                                multiline
                                rows={4}
                                inputProps={{ maxLength: 250 }}
                                placeholder={t('form.description')}
                                variant="outlined"
                                disabled={isDescriptionDisabled}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            className="bidSetupSelection"
                        >
                            <AutoComplete
                                id="admin-config-select"
                                key={`bidSetUp_${bidSetupId}`}
                                value={selectedSetup}
                                options={createAccessSetups}
                                // @ts-ignore
                                onChange={(event, optionSelected) => handleBidSetupChange(Fields.BidSetupId, optionSelected?.bidSetupId)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label={t('form.bidSetup')}
                                    />
                                )}
                                getOptionLabel={(option) => option.name}
                                includeInputInList
                                disabled={isBidSetupDisabled}
                                className={validationErrors.bidSetupId ? 'form-input-error' : ''}
                            />
                            {
                                validationErrors.bidSetupId ? <span style={{ color: 'red' }}>{t('form.error.bidSetup')}</span> : null
                            }
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        xs={6}
                    >
                        <SetupPreview
                            bidSetupId={bidSetupId}
                        />
                    </Grid>
                </Grid>

            </AccordionDetails>
            {!hasOnlyViewAccess && (
                <>
                    <Divider />
                    <AccordionActions>
                        <Button
                            color="secondary"
                            variant="contained"
                            onClick={() => handleDiscard()}
                            disabled={isInputDisabled && isDescriptionDisabled}
                        >
                            {t('globalForm.discard')}
                        </Button>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={() => handleConfirm()}
                            disabled={isInputDisabled && isDescriptionDisabled}
                            className="save-continue--button"
                        >
                            <FontAwesomeIcon icon={faCheckCircle} />
                            {t('globalForm.applyButton')}
                        </Button>
                    </AccordionActions>
                </>
            )}
        </Accordion>
    );
}

export default BaseInfo;
